import { Activity, ListFilter, ShieldCheck, Star } from "lucide-react";
import type { BuiltInViewId } from "../lib/types";
import { cn } from "../lib/utils";
import { ThemeToggle } from "./ThemeToggle";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

interface NavigationRailProps {
  activeViewId: BuiltInViewId;
  coverageOpen: boolean;
  onSelectScreener: () => void;
  onSelectWatchlist: () => void;
  onOpenCoverage: () => void;
}

interface NavigationButtonProps {
  label: string;
  active: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}

function NavigationButton({ label, active, icon, onClick }: NavigationButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-current={active ? "page" : undefined}
          className={cn(
            "relative flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-accent-soft hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            active && "bg-accent-soft text-accent"
          )}
          onClick={onClick}
        >
          {active ? (
            <span
              aria-hidden="true"
              className="absolute -left-1 h-5 w-0.5 rounded-r bg-accent"
            />
          ) : null}
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

export function NavigationRail({
  activeViewId,
  coverageOpen,
  onSelectScreener,
  onSelectWatchlist,
  onOpenCoverage
}: NavigationRailProps) {
  return (
    <aside className="flex h-screen w-12 shrink-0 flex-col items-center border-r border-border bg-surface py-2">
      <div
        aria-hidden="true"
        className="flex h-9 w-9 items-center justify-center rounded-md border border-accent bg-accent-soft text-accent"
      >
        <Activity className="h-4 w-4" />
      </div>

      <TooltipProvider delayDuration={150}>
        <nav aria-label="Primary navigation" className="mt-4 flex flex-col gap-1">
          <NavigationButton
            label="Screener"
            active={activeViewId !== "my-watchlist" && !coverageOpen}
            icon={<ListFilter className="h-4 w-4" aria-hidden="true" />}
            onClick={onSelectScreener}
          />
          <NavigationButton
            label="My watchlist"
            active={activeViewId === "my-watchlist" && !coverageOpen}
            icon={<Star className="h-4 w-4" aria-hidden="true" />}
            onClick={onSelectWatchlist}
          />
          <NavigationButton
            label="Coverage"
            active={coverageOpen}
            icon={<ShieldCheck className="h-4 w-4" aria-hidden="true" />}
            onClick={onOpenCoverage}
          />
        </nav>
      </TooltipProvider>

      <div className="mt-auto">
        <ThemeToggle />
      </div>
    </aside>
  );
}
